import type { FAQItem, FAQCategory } from './types';

export const physicianFAQ: FAQItem[] = [
  {
    id: 'physician-faq-eligibility-1',
    question: 'Who is eligible for EQuIP?',
    response:
      'Eligibility is based on three criteria: (1) Full-time or part-time employment status, (2) Primary Care or Psychiatry specialty, and (3) Productivity-based pay (not salaried). All criteria must be met on Day 1 of the quarter and at the end of the quarter.',
    keyPoints: [
      'Must be full-time or part-time (not PRN)',
      'Specific specialties only: Primary Care or Psychiatry',
      'Must be on productivity pay, not salary',
      'Criteria evaluated at start and end of quarter',
    ],
    category: 'eligibility',
  },
  {
    id: 'physician-faq-eligibility-2',
    question: 'What happens if I switch from productivity pay to salary mid-quarter?',
    response:
      'If you switch to salaried compensation during a quarter, you are no longer eligible for that quarter\'s incentive. Eligibility requires productivity-based pay at both the start and end of the quarter.',
    keyPoints: [
      'Switching to salary will result in ineligibility for that quarter',
      'Must be on productivity pay for entire quarter',
      'No partial eligibility for pay type changes',
    ],
    category: 'eligibility',
  },
  {
    id: 'physician-faq-eligibility-3',
    question: 'What if I switch from full-time to part-time during the quarter?',
    response:
      'You remain eligible for an incentive, but it will be determined as a part-time award. Your average days worked will be calculated using a rolling 90-day average from the date of your status change.',
    keyPoints: [
      'Still eligible, but for part-time award only',
      'Average days calculated from status change date',
      'Uses rolling 90-day average',
    ],
    category: 'eligibility',
  },
  {
    id: 'physician-faq-eligibility-4',
    question: 'Am I eligible if I\'m terminated before the quarter ends?',
    response:
      'No. If your employment ends before the quarter closes, you are not eligible for that quarter\'s incentive. However, if you are terminated after the quarter ends, you are still eligible for the payout.',
    keyPoints: [
      'Terminated before quarter end = not eligible',
      'Terminated after quarter end = eligible for payout',
      'Employment must continue through quarter close',
    ],
    category: 'eligibility',
  },
  {
    id: 'physician-faq-1',
    question: 'Why are these the measures being used?',
    response:
      'These measures reflect our company goals for 2026 and our focus on outcomes-centered healthcare. They also reflect what we can reliably measure today. We are being intentional about grounding the program in data we can stand behind, rather than inferred or subjective metrics. As our analytics mature, the measures will evolve.',
    keyPoints: [
      'Alignment with company goals and Outcomes-Centered Healthcare',
      'Fairness and measurability',
      'Future evolution is expected',
    ],
    category: 'measures',
  },
  {
    id: 'physician-faq-2',
    question: 'The 90% threshold feels too high.',
    response:
      'The 90% threshold is applied consistently across measures and is aligned with external benchmarks. For the ACO Visits eligibility requirement, Q1 uses a 70% threshold to support initial alignment before moving to 90% in Q2. Once the eligibility requirement is met, performance across other measures is balanced so that strength in one area can offset an unmet measure in another.',
    keyPoints: [
      'Consistency across measures',
      'Aligned with external benchmarks',
      'ACO Visits uses 70% in Q1, 90% in Q2+',
      'Diversification across non-gate measures reduces risk',
    ],
    category: 'thresholds',
  },
  {
    id: 'physician-faq-3',
    question: "I did not meet one measure. Does that mean I'm not eligible for any incentive?",
    response:
      'It depends on which measure. The ACO Visits measure serves as an eligibility requirement that must be met to unlock incentive eligibility. If this requirement is met, performance on other measures is independent, and missing one does not eliminate the entire incentive opportunity. If the ACO Visits requirement is not met, incentive eligibility is not available for that quarter.',
    keyPoints: [
      'ACO Visits requirement must be met to unlock any incentive',
      'Other measures remain independent once eligibility is established',
      'Each quarter provides a fresh opportunity',
    ],
    category: 'general',
  },
  {
    id: 'physician-faq-4',
    question: 'The ACO Visits measure feels out of my control.',
    response:
      'We understand this concern. The measure is scoped specifically to your assigned facilities and your assigned ACO patients, keeping the focus on care you can directly influence. In Q1, the threshold is set at 70% to allow time for alignment. Starting in Q2, the threshold increases to 90%. Physicians with no assigned ACO patients are automatically credited with meeting this requirement.',
    keyPoints: [
      'Limited to assigned facilities and assigned ACO patients',
      'Q1 threshold is 70% to support initial alignment',
      'Q2 and beyond threshold is 90%',
      'Physicians with no ACO patients automatically meet the requirement',
    ],
    category: 'measures',
  },
  {
    id: 'physician-faq-5',
    question: "I didn't know where I stood until the quarter ended.",
    response:
      'Performance visibility is available during the quarter. Leaders are expected to encourage physicians to review their individual data early and consistently so there are no surprises at the end.',
    keyPoints: [
      'Shared responsibility for visibility',
      'Data is available before quarter close',
      'Goal is no surprises',
    ],
    category: 'process',
  },
  {
    id: 'physician-faq-6',
    question: "This doesn't reflect the quality of care I provide.",
    response:
      'The incentive metrics are focused on specific outcomes that we can reliably measure. It is not intended to capture the full impact you have on your patients.',
    keyPoints: [
      'Focused on reliably measurable outcomes',
      'Does not capture full clinical impact',
      'Opportunity to adjust going forward',
    ],
    category: 'general',
  },
  {
    id: 'physician-faq-7',
    question: "Why can't there be exceptions?",
    response:
      'To ensure fairness and consistency, the incentive program is applied uniformly across the organization. Leaders will review incentive payouts to confirm accuracy, and any discrepancies will be corrected by our centralized team. Exceptions for individual circumstances are not permitted to ensure consistent application of the program.',
    keyPoints: [
      'Fairness through consistency',
      'Leaders should not promise exceptions',
      'HR manages exception review',
    ],
    category: 'exceptions',
  },
  {
    id: 'physician-faq-8',
    question: 'Will these measures change again next quarter?',
    response:
      'The Consistency measures are set for the year to provide stability. If changes are needed in the future, they will be communicated clearly and in advance.',
    keyPoints: ['Predictability', 'Advance communication', 'No surprise changes'],
    category: 'process',
  },
  {
    id: 'physician-faq-9',
    question: "What should I focus on if I didn't earn the full incentive?",
    response:
      'Start with the ACO Visits eligibility requirement. Meeting this threshold is the first step to unlocking incentive eligibility. Once the requirement is met, the data helps identify which other measures to prioritize. Each quarter is a reset and an opportunity to adjust.',
    keyPoints: [
      'Prioritize meeting the ACO Visits requirement first',
      'Use data to identify other areas for improvement',
      'Each quarter is a fresh opportunity',
      'Forward-looking, improvement-focused approach',
    ],
    category: 'general',
  },
  {
    id: 'physician-faq-10',
    question: 'When should questions be escalated?',
    response:
      'Questions related to program design, thresholds, or calculations should be directed to HR@eventuswh.com. Leaders focus should remain on supporting understanding and reinforcing outcomes-centered care.',
    keyPoints: [
      'Program design questions go to HR@eventuswh.com',
      'Leaders focus on support and understanding',
    ],
    category: 'process',
  },
  {
    id: 'physician-faq-gate-1',
    question: 'What is the ACO Visits eligibility requirement?',
    response:
      'The ACO Visits measure functions as an eligibility requirement for physician incentives. This means it must be met before any other measure performance counts toward the incentive. The requirement reflects the organizational priority of maintaining consistent care for ACO patients at assigned facilities.',
    keyPoints: [
      'ACO Visits is an eligibility requirement, not a weighted component',
      'Must be met to unlock incentive eligibility',
      'Reflects organizational commitment to ACO patient care',
      'Other measures count only after requirement is met',
    ],
    category: 'measures',
  },
  {
    id: 'physician-faq-gate-2',
    question: 'What happens if I do not meet the ACO Visits requirement?',
    response:
      'If the ACO Visits eligibility requirement is not met, incentive eligibility is not available for that quarter. Performance on other measures does not factor in until the requirement is satisfied. Each quarter is independent, so not meeting the requirement in one quarter does not affect future quarters.',
    keyPoints: [
      'Requirement not met = no incentive eligibility for that quarter',
      'Other measure performance does not compensate for unmet requirement',
      'Each quarter provides a new opportunity',
      'Focus on meeting the requirement first',
    ],
    category: 'measures',
  },
  {
    id: 'physician-faq-gate-3',
    question: 'Why do physicians have an eligibility requirement when APCs do not?',
    response:
      'The eligibility requirement reflects different organizational expectations based on role. Physicians have primary accountability for ACO patient panels and care continuity at assigned facilities. This structure aligns the incentive program with those expectations. APCs, with different panel structures and supervisory relationships, follow a different framework.',
    keyPoints: [
      'Reflects role-based accountability differences',
      'Physicians have primary ACO panel responsibility',
      'Program design aligns incentives with expectations',
      'Each role has a framework designed for its context',
    ],
    category: 'general',
  },
  {
    id: 'physician-faq-gate-4',
    question: 'How is Q1 different from other quarters?',
    response:
      'Q1 uses a 70% threshold for the ACO Visits eligibility requirement to allow time for workflow alignment. Starting in Q2 and continuing through the year, the threshold increases to 90%. Additionally, Q1 focuses on consistency measures only, while Q2 and beyond include both outcome and consistency measures in the 60/40 model.',
    keyPoints: [
      'Q1 threshold: 70%',
      'Q2, Q3, Q4 threshold: 90%',
      'Q1 allows time for alignment',
      'Q1 is consistency measures only; Q2+ adds outcomes',
    ],
    category: 'thresholds',
  },
  {
    id: 'physician-faq-gate-5',
    question: 'What does "Met" or "Unmet" mean for the ACO Visits requirement?',
    response:
      'The ACO Visits eligibility requirement uses "Met" and "Unmet" terminology. "Met" indicates the threshold was reached and incentive eligibility is unlocked. "Unmet" indicates the threshold was not reached and incentive eligibility is not available for that quarter. This language reflects a status check rather than a judgment.',
    keyPoints: [
      '"Met" = threshold reached, eligibility unlocked',
      '"Unmet" = threshold not reached, eligibility not available',
      'Terminology reflects status, not judgment',
      'Each quarter is evaluated independently',
    ],
    category: 'general',
  },
];

export const physicianFAQCategories: { id: FAQCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'general', label: 'General' },
  { id: 'measures', label: 'Measures' },
  { id: 'thresholds', label: 'Thresholds' },
  { id: 'exceptions', label: 'Exceptions' },
  { id: 'process', label: 'Process' },
];
